// src/components/common/RowDeleteAction.tsx
/**
 * Deletes a single record from a queue row, behind the same gates as the bulk flow.
 *
 * ⚠️ Governed by the module's **`AllowDelete.<Scope>`** setting, which is separate from
 * `AllowBulkDelete.<Scope>` on purpose: an office may reasonably want to remove one piece of spam
 * without enabling a control that can clear a page at a time.
 *
 * ⚠️ Still requires typing the confirmation word. A single delete is smaller than a bulk one but no
 * more reversible, and this sits in a table row where the click target is a few pixels from "open".
 */
import { useState } from 'react';
import { ActionIcon, Tooltip } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { AccessControl } from '@/components';
import { toast } from '@/components/ui/providers/AlertProvider';
import { classifyDeleteError } from '@/utils/deleteError';
import TypeToConfirmModal from './TypeToConfirmModal';

export interface RowDeleteActionProps {
  /** The record's id. */
  id: number;
  /** Singular noun — "intake", "request", "case", "registration". */
  entityLabel: string;
  /** How the row is named in the confirmation, e.g. a reference. */
  recordLabel?: string | null;
  confirmWord: string;
  /** The module setting. False hides the action entirely. */
  allowed: boolean;
  /** False when the SDK has no delete method — shown disabled with a reason. */
  supported: boolean;
  /**
   * Why this particular row cannot be deleted, e.g. it has already been approved. Shown disabled with
   * this as the tooltip.
   *
   * ⚠️ Better than letting the click through to a **400**: the server refuses an approved record
   * because a registration or case now exists behind it, and that is a permanent state, not a
   * transient failure. Saying so up front beats a red toast after a typed confirmation.
   */
  blockedReason?: string | null;
  /**
   * Whether the server keeps the record after deleting it. **All four AC queues are soft-deleted** as
   * of the 2026-08-26 backend change. See `BulkDeleteControls` for why this is still a prop.
   */
  recoverable?: boolean;
  /** Should **throw** on failure — this component words the error and keeps the dialog open. */
  onDelete: (id: number) => Promise<void>;
}

export default function RowDeleteAction({
  id, entityLabel, recordLabel, confirmWord, allowed, supported,
  blockedReason, recoverable = false, onDelete,
}: RowDeleteActionProps) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!allowed) return null;

  const disabled = !supported || Boolean(blockedReason);

  return (
    <AccessControl roles={['ActsCareAdmin']}>
      <Tooltip
        label={blockedReason
          || (supported
            ? `Delete this ${entityLabel}`
            : `Deleting ${entityLabel}s is not available in this API version yet`)}
        withArrow
      >
        {/* Wrapped so the tooltip still fires while disabled. */}
        <span>
          <ActionIcon
            variant="light"
            color="red"
            disabled={disabled}
            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </span>
      </Tooltip>

      <TypeToConfirmModal
        opened={open}
        onClose={() => setOpen(false)}
        title={`Delete ${recordLabel || `this ${entityLabel}`}`}
        confirmWord={confirmWord}
        count={1}
        busy={busy}
        actionLabel={`Delete ${entityLabel}`}
        warning={recoverable
          ? (
            <>
              This {entityLabel} disappears from the queue and from the dashboard totals, and there is
              no way to bring it back from this screen. The record is kept in the database, so ask the
              system team if it goes by mistake.
            </>
          )
          : (
            <>
              This cannot be undone. Once deleted this {entityLabel} cannot be retrieved, and anything
              already created from it will lose the link back.
            </>
          )}
        onConfirm={async () => {
          setBusy(true);
          try {
            await onDelete(id);
            setOpen(false);
          } catch (e) {
            console.error(`[RowDeleteAction] deleting ${entityLabel} ${id} failed:`, e);
            // ⚠️ The dialog stays open. The admin typed a word to get here; dropping them back to the
            // list with a toast leaves them unsure whether the row went.
            const failure = classifyDeleteError(e, entityLabel);
            toast.error(failure.title, failure.message);
          } finally {
            setBusy(false);
          }
        }}
      />
    </AccessControl>
  );
}
